import { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { useCategories } from "@/context/CategoriesContext";
import { BoardIndex, BoardMasonry } from "@/components/BoardThemes";
import api from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

export default function Castings() {
  usePageMeta({
    title: "Casting Calls — Open Model Castings | Solis Casting",
    description: "Live casting calls from houses, magazines and brands — runway, editorial, commercial and film. Apply with your Solis Casting book.",
    url: `${window.location.origin}/castings`,
  });
  const [castings, setCastings] = useState([]);
  const [city, setCity] = useState("All");
  const [cat, setCat] = useState("All");
  const [loading, setLoading] = useState(true);
  const { categories: liveCategories } = useCategories();

  useEffect(() => {
    api.get("/castings").then((r) => setCastings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const cities = useMemo(() => ["All", ...new Set(castings.map((c) => c.city).filter(Boolean))], [castings]);
  const filtered = castings.filter(
    (c) => (city === "All" || c.city === city) && (cat === "All" || (c.categories || []).includes(cat))
  );
  const withImages = filtered.filter((c) => c.image_url);
  const withoutImages = filtered.filter((c) => !c.image_url);

  return (
    <div className="px-6 sm:px-10 py-24" data-testid="castings-page">
      <Reveal>
        <p className="overline-label mb-4">Now Casting</p>
        <h1 className="font-display font-light text-5xl sm:text-7xl tracking-tighter mb-16">
          Casting <span className="italic text-accent">Calls</span>
        </h1>
      </Reveal>

      <div className="flex flex-wrap items-center gap-x-10 gap-y-4 mb-16 border-y border-white/10 py-6" data-testid="castings-filters">
        {[
          { label: "City", value: city, set: setCity, options: cities, tid: "casting-city-select" },
          { label: "Category", value: cat, set: setCat, options: ["All", ...liveCategories], tid: "casting-category-select" },
        ].map(({ label, value, set, options, tid }) => (
          <div key={label} className="flex items-center">
            <span className="overline-label !text-[10px] mr-4">{label}</span>
            <div className="relative">
              <select
                data-testid={tid}
                value={value}
                onChange={(e) => set(e.target.value)}
                className="appearance-none bg-background border border-white/20 hover:border-white/60 transition-colors duration-300 font-grotesk text-xs uppercase tracking-[0.15em] pl-4 pr-10 py-3 outline-none cursor-pointer"
              >
                {options.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-white/50" />
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <p className="overline-label" data-testid="castings-loading">Opening the calls…</p>
      ) : filtered.length === 0 ? (
        <p className="font-display italic text-3xl text-white/40" data-testid="castings-empty">The room is quiet — check back soon.</p>
      ) : (
        <>
          {withImages.length > 0 && <BoardMasonry castings={withImages} />}
          {withoutImages.length > 0 && (
            <div className={withImages.length > 0 ? "mt-24" : ""}>
              {withImages.length > 0 && (
                <Reveal>
                  <p className="overline-label mb-8" data-testid="details-only-label">Further calls — details only</p>
                </Reveal>
              )}
              <BoardIndex castings={withoutImages} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
